import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import GoLiveRoom from "../../components/GoLiveRoom/GoLiveRoom";
import LiveNowViewerRoom from "../../components/LiveNowViewerRoom/LiveNowViewerRoom";
import { CASE_DISCUSSIONS_ROUTES } from "../../routePaths";
import "./Medicalcaselive.css";
import { fetchLiveById } from "../../redux/thunks/medicalCasesThunks";
import {
  selectCurrentLive,
  selectCurrentLiveStatus,
  selectCurrentLiveError,
} from "../../redux/selectors/medicalCasesSelectors";
import { getCurrentDoctor } from "../../../authDoctor/redux/thunks/doctorsThunks";
import {
  getDoctorIdentityIds,
  isLiveHost,
} from "../../utils/liveControls";

function isUuid(value) {
  return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function normalizeLiveRoomName(value, fallbackId) {
  const raw = String(value || "").trim();
  if (raw) {
    const normalized = raw
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (normalized) return normalized;
  }

  const fallback = String(fallbackId || "").trim();
  if (!fallback) return "medical-live";

  const safeFallback = fallback
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

  return `medical-live-${safeFallback || "stream"}`;
}

export default function MedicalCaseLive() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentDoctor = useSelector((state) => state?.doctor?.user || null);
  const stateStreamData = location?.state?.streamData || null;
  const currentLive = useSelector((state) => selectCurrentLive(state));
  const currentLiveStatus = useSelector((state) => selectCurrentLiveStatus(state));
  const currentLiveError = useSelector((state) => selectCurrentLiveError(state));
  const invalidLiveId = Boolean(id && !isUuid(id));
  const currentDoctorIds = useMemo(
    () => getDoctorIdentityIds(currentDoctor),
    [currentDoctor]
  );

  useEffect(() => {
    if (currentDoctorIds.length === 0) dispatch(getCurrentDoctor());
  }, [currentDoctorIds.length, dispatch]);

  // BUGFIX: this used to bail out early with `if (stateStreamData) return;`,
  // which meant that whenever a doctor navigated here right after creating
  // the live (the normal flow — GoLiveStreamForm always passes
  // `state.streamData`), the app would NEVER fetch fresh data from Redux.
  // `currentLive` in the store would still get updated correctly whenever
  // start/end/cancel/join/leave succeeded (those thunks set
  // `currentLive.data` in the slice), but this page would keep ignoring it
  // and rendering the stale `stateStreamData` snapshot forever — so the
  // Start/End/Cancel buttons in GoLiveRoom never switched state after being
  // clicked. We now always fetch when we have a valid id, so `currentLive`
  // stays in sync regardless of how the page was reached.
  useEffect(() => {
    if (!id || !isUuid(id)) return;
    dispatch(fetchLiveById(id));
  }, [id, dispatch]);

  const streamData = useMemo(() => {
    const base =
      stateStreamData || {
        id,
        title: `Live Stream ${id || ""}`,
        description: "",
        specialty: "General",
        watchingCount: 3,
      };

    const roomName = normalizeLiveRoomName(
      base?.roomName || currentLive?.roomName || currentLive?.room_name,
      id
    );

    const currentLiveId = currentLive?.id || currentLive?._id;
    if (currentLive && String(currentLiveId) === String(id)) {
      // Merge fresh Redux data (status, participants, roomName, etc.) on top of the
      // static base snapshot, but keep `isHost` from the base/local
      // knowledge since the backend payload doesn't carry that flag —
      // it's derived client-side from how the user reached this page.
      return {
        ...base,
        ...currentLive,
        roomName,
      };
    }

    return { ...base, roomName };
  }, [id, stateStreamData, currentLive]);

  const hostDoctorId = streamData?.hostDoctorId || streamData?.host_doctor_id;
  const hostModeKnown = Boolean(hostDoctorId) && currentDoctorIds.length > 0;
  const isHostMode = isLiveHost(hostDoctorId, currentDoctor);

  const handleEndLiveStream = () => {
    navigate(CASE_DISCUSSIONS_ROUTES.LIST);
  };

  return (
    <AppLayoutDoctor>
      <div className="mcd-live-page">
        {!hostModeKnown && currentLiveStatus === "failed" ? (
          <div className="mcd-live-error">Impossible de vérifier votre rôle : {currentLiveError}</div>
        ) : !hostModeKnown ? (
          <div className="mcd-live-loading">Vérification de votre rôle dans le live…</div>
        ) : currentLiveStatus === "loading" && !streamData?.title ? (
          <div className="mcd-live-loading">Chargement du live…</div>
        ) : invalidLiveId ? (
          <div className="mcd-live-error">Identifiant de live invalide.</div>
        ) : currentLiveError && !streamData?.title ? (
          <div className="mcd-live-error">Erreur: {currentLiveError}</div>
        ) : isHostMode ? (
          <GoLiveRoom streamData={streamData} doctor={currentDoctor} onEnd={handleEndLiveStream} />
        ) : (
          <LiveNowViewerRoom streamData={streamData} doctor={currentDoctor} />
        )}
      </div>
    </AppLayoutDoctor>
  );
}
